/**
 * Design Tokens: Colors
 *
 * Single enterprise theme. Warm neutrals, coral/orange accent,
 * clean and professional.
 */

export const colors = {
  // ── Background ────────────────────────────────────────────────
  background: {
    page: '#F5F3EF',
    primary: '#F5F3EF',
    card: '#FFFFFF',
    cardHover: '#FAFAF8',
    secondary: '#F0EDEA',
    header: '#FFFFFF',
    sidebar: '#F5F3EF',
    input: '#FFFFFF',
    inputHover: '#FAFAF8',
    highlight: '#FFF5F0',
    overlay: 'rgba(0, 0, 0, 0.45)',
    surface: '#FAFAF8',
    subtle: '#FAFAF8',
    muted: '#F8FAFC',
    code: '#F5F3EF',
    codeBlock: '#1E1E1E',
  },

  // ── Text ──────────────────────────────────────────────────────
  text: {
    primary: '#1A1A1A',
    secondary: '#6B7280',
    tertiary: '#667085',
    disabled: '#98A2B3',
    onPrimary: '#FFFFFF',
    onDark: '#FFFFFF',
    link: '#E8734A',
    heading: '#0F172A',
    caption: '#64748B',
    muted: '#94A3B8',
    code: '#D63384',
  },

  // ── Accent / Primary ─────────────────────────────────────────
  accent: {
    main: '#E8734A',
    primary: '#E8734A',
    primarySolid: '#E8734A',
    hover: '#D4623B',
    active: '#C05530',
    light: '#FFF0EB',
    lighter: '#FEE4D9',
    contrast: '#FFFFFF',
    secondary: '#D4623B',
    secondarySolid: '#D4623B',
    dark: '#D4623B',
    // Semantic aliases so call sites can reach status colors via the accent palette
    success: '#10B981',
    warning: '#F59E0B',
    info: '#3B82F6',
  },

  // ── Neutral / Charcoal ───────────────────────────────────────
  neutral: {
    900: '#1A1A1A',
    800: '#1F2937',
    700: '#374151',
    600: '#4B5563',
    500: '#6B7280',
    400: '#9CA3AF',
    300: '#D1D5DB',
    200: '#E5E7EB',
    100: '#F3F4F6',
    50: '#F9FAFB',
  },

  // ── Slate palette (for data-heavy UIs) ───────────────────────
  slate: {
    900: '#0F172A',
    800: '#1E293B',
    700: '#334155',
    600: '#475569',
    500: '#64748B',
    400: '#94A3B8',
    300: '#CBD5E1',
    200: '#E2E8F0',
    100: '#F1F5F9',
    50: '#F8FAFC',
  },

  // ── Border ────────────────────────────────────────────────────
  border: {
    primary: '#E5E7EB',
    subtle: '#F0F0EE',
    hover: '#D1D5DB',
    focus: '#E8734A',
    input: '#E5E7EB',
    divider: '#E2E8F0',
    info: '#3B82F6',
  },

  // ── Overlay ───────────────────────────────────────────────────
  overlay: {
    subtle: 'rgba(0, 0, 0, 0.02)',
    medium: 'rgba(0, 0, 0, 0.04)',
    strong: 'rgba(0, 0, 0, 0.08)',
    heavy: 'rgba(0, 0, 0, 0.16)',
    white: {
      subtle: 'rgba(255, 255, 255, 0.06)',
      medium: 'rgba(255, 255, 255, 0.1)',
      strong: 'rgba(255, 255, 255, 0.3)',
      heavy: 'rgba(255, 255, 255, 0.8)',
    },
  },

  // ── Shadow ────────────────────────────────────────────────────
  shadow: {
    primary: 'none',
    color: 'rgba(0, 0, 0, 0.06)',
    colorHover: 'rgba(0, 0, 0, 0.1)',
  },

  // ── Status / Semantic ─────────────────────────────────────────
  status: {
    success: '#10B981',
    successLight: '#D1FAE5',
    warning: '#F59E0B',
    warningLight: '#FEF3C7',
    error: '#EF4444',
    errorLight: '#FEE2E2',
    danger: '#EF4444',
    info: '#3B82F6',
    infoLight: '#DBEAFE',
  },

  success: {
    main: '#10B981',
    light: '#D1FAE5',
    dark: '#059669',
  },
  warning: {
    main: '#F59E0B',
    light: '#FEF3C7',
    dark: '#D97706',
  },
  error: {
    main: '#EF4444',
    light: '#FEE2E2',
    dark: '#DC2626',
  },
  info: {
    main: '#3B82F6',
    light: '#DBEAFE',
    dark: '#2563EB',
  },

  primary: {
    main: '#E8734A',
    light: '#FFF0EB',
    dark: '#D4623B',
  },
  secondary: {
    main: '#1F2937',
    light: '#4B5563',
    dark: '#1A1A1A',
  },

  // ── Complexity / Severity levels ──────────────────────────────
  complexity: {
    low: '#059669',
    lowBg: '#05966915',
    medium: '#D97706',
    mediumBg: '#D9770615',
    high: '#DC2626',
    highBg: '#DC262615',
    critical: '#7C3AED',
    criticalBg: '#7C3AED15',
  },

  // ── Category colors (for entity/relationship types) ──────────
  category: {
    teal: '#0D9488',
    magenta: '#C026D3',
    violet: '#7C3AED',
    purple: '#8B5CF6',
    indigo: '#6366F1',
    blue: '#3B82F6',
    green: '#10B981',
    amber: '#D97706',
    red: '#DC2626',
  },

  // ── Chart palette ─────────────────────────────────────────────
  chart: {
    primary: '#E8734A',
    secondary: '#9CA3AF',
    tertiary: '#D1D5DB',
    quaternary: '#374151',
    series: ['#E8734A', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'],
  },

  // ── Workspace / Project status ────────────────────────────────
  workspace: {
    active: '#10B981',
    archived: '#9CA3AF',
    draft: '#F59E0B',
  },
  project: {
    planning: '#9CA3AF',
    inProgress: '#F59E0B',
    testing: '#3B82F6',
    completed: '#10B981',
    cancelled: '#EF4444',
  },

  // ── Gradients ─────────────────────────────────────────────────
  gradients: {
    primary: '#E8734A',
    warmCard: 'linear-gradient(135deg, #FFF5F0 0%, #FFFFFF 100%)',
    accentSoft: 'linear-gradient(135deg, #FEE4D9 0%, #FFF0EB 100%)',
    hero: 'linear-gradient(135deg, #FFFFFF 0%, #F6F8FA 50%, #FFFFFF 100%)',
    vbnet: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    rpg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    cobol: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    database: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
  },

  // ── Glass (for liquid glass components on dark backgrounds) ──
  glass: {
    text: '#FFFFFF',
    textMuted: 'rgba(255, 255, 255, 0.8)',
    border: 'rgba(255, 255, 255, 0.3)',
    borderHover: '#FFFFFF',
    background: 'rgba(255, 255, 255, 0.1)',
  },
} as const

export type BackgroundColor = keyof typeof colors.background
export type TextColor = keyof typeof colors.text
export type StatusColor = keyof typeof colors.status
export type ColorTheme = 'light' | 'dark'

export const getColor = (path: string): string => {
  const parts = path.split('.')
  let current: any = colors
  for (const key of parts) {
    if (current === undefined) return ''
    current = current[key]
  }
  if (typeof current === 'string') return current
  return ''
}

export const getThemeColor = (
  colorPathOrTheme: string,
  category?: string,
  shade?: string,
): string => {
  // 3-arg form: getThemeColor(theme, category, shade) — theme/mode is ignored
  // (palette is mode-agnostic today), category.shade resolves the token path.
  if (category !== undefined && shade !== undefined) {
    return getColor(`${category}.${shade}`)
  }
  // 1-arg form: getThemeColor('text.secondary')
  return getColor(colorPathOrTheme)
}

export default colors
